import { apiGet, apiPost } from './client';

/**
 * Self-test API: lets a signed-in user measure our policy layer themselves.
 *
 * These are two different quantities and the UI must not merge them:
 *
 *   - `PolicyOverheadSelfTest` measures policy evaluation alone. It is
 *     sub-millisecond, runs on demand, and works for a brand-new org with no
 *     traffic.
 *   - `MeasuredOverhead` is the whole service-side layer, recomputed from the
 *     org's own retrieval records. That is the figure published at
 *     gateco.ai/benchmarks, and it is dominated by database round trips.
 */

export interface PolicyOverheadSelfTest {
  iterations: number;
  warmup_iterations: number;
  candidates_per_iteration: number;
  candidates_allowed: number;
  candidates_denied: number;
  policies_evaluated: number;
  rules_evaluated: number;
  policy_source: 'organization' | 'reference';
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  min_ms: number;
  max_ms: number;
  mean_ms: number;
  measured: string;
  excluded: string[];
}

export interface MeasuredOverhead {
  sample_count: number;
  window_days: number;
  p50_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
  connector_p50_ms: number | null;
  total_p50_ms: number | null;
  measured: string;
}

/** Run the live policy-engine self-test. Costs the caller nothing. */
export async function runPolicyOverheadSelfTest(
  iterations = 150
): Promise<PolicyOverheadSelfTest> {
  const response = await apiPost<{ data: PolicyOverheadSelfTest }>(
    `/benchmark/policy-overhead?iterations=${iterations}`
  );
  return response.data;
}

/** Recompute the published figure from this org's own retrievals. */
export async function getMeasuredOverhead(windowDays = 30): Promise<MeasuredOverhead> {
  const response = await apiGet<{ data: MeasuredOverhead }>('/benchmark/measured-overhead', {
    window_days: windowDays,
  });
  return response.data;
}
